import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Icon } from '@reservamos/elements';
import PropTypes from 'prop-types';

/**
 * TaxpayerInfo component.
 *
 * This component displays taxpayer information in a card format.
 *
 * @param {object} props - taxpayerData
 * @param {object} props.taxpayerData - Taxpayer data object with legalName and address
 *
 * @returns {JSX.Element} The rendered taxpayer information component.
 */
const TaxpayerInfo = ({ taxpayerData }) => {
  const { t } = useTranslation('exchange');

  if (!taxpayerData) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 shadow-sm">
      <div className="flex items-center mb-4">
        <div className="flex-shrink-0">
          <Icon type="CircleCheckmark" size="M" color="success" />
        </div>
        <div className="ml-3">
          <Text size="L" weight="bold" color="success">
            {t('modal_confirm_billing.taxpayer_info_found')}
          </Text>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white rounded-lg p-4 border border-gray-100 transition-all duration-200 hover:shadow-md">
          <div className="flex-1">
            <Text size="S" weight="semibold" className="text-gray-600 mb-1">
              {t('labels.legal_name')}
            </Text>
            <Text size="M" className="text-gray-900 font-medium">
              {taxpayerData.legalName}
            </Text>
          </div>
        </div>

        {taxpayerData.address && (
          <div className="bg-white rounded-lg p-4 border border-gray-100 transition-all duration-200 hover:shadow-md">
            <div className="flex-1">
              <Text size="S" weight="semibold" className="text-gray-600 mb-1">
                {t('labels.address')}
              </Text>
              <Text size="M" className="text-gray-900 font-medium">
                {taxpayerData.address}
              </Text>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

TaxpayerInfo.propTypes = {
  taxpayerData: PropTypes.shape({
    legalName: PropTypes.string.isRequired,
    address: PropTypes.string,
  }),
};

export default TaxpayerInfo;
